<template>
    <div>
        <FormLabel 
            v-if="!hideLabel"
            :for="fieldId" 
            :required="required"
        >{{ label }}</FormLabel>

        <div class="tw-relative">
            <!-- Main selector button -->
            <button
                type="button"
                :id="fieldId"
                @click="toggleDropdown"
                :class="[
                    'tw-w-full tw-h-[40px] tw-outline-none tw-text-sm tw-rounded-md tw-border tw-border-solid tw-px-4 tw-duration-200 tw-flex tw-items-center tw-justify-between tw-bg-white',
                    'focus:tw-border-primary-500 hover:tw-border-gray-300',
                    !!error && '!tw-border-red-500',
                    isOpen && 'tw-border-primary-500',
                    $root.$i18n.locale == 'ar' && 'tw-text-right'
                ]"
            >
                <div class="tw-flex tw-items-center tw-gap-3">
                    <icon 
                        v-if="leftIcon"
                        :icon="leftIcon" 
                        :class="[
                            'tw-text-xl tw-text-gray-500 tw-duration-200',
                            !!error && '!tw-text-red-500',
                            isOpen && 'tw-text-primary-500'
                        ]"
                    />
                    <span :class="[
                        selectedType ? 'tw-text-gray-900' : 'tw-text-gray-500',
                        'tw-font-medium'
                    ]">
                        {{ selectedType ? t(`account-types.${selectedType}.title`) : placeholder }}
                    </span>
                </div>

                <icon 
                    icon="solar:alt-arrow-down-outline" 
                    :class="[
                        'tw-text-xl tw-text-gray-500 tw-duration-200 tw-transform',
                        !!error && '!tw-text-red-500',
                        isOpen && 'tw-rotate-180 tw-text-primary-500'
                    ]" 
                />
            </button>

            <!-- Dropdown menu -->
            <transition
                enter-active-class="tw-transition tw-duration-200 tw-ease-out"
                enter-from-class="tw-transform tw-scale-95 tw-opacity-0"
                enter-to-class="tw-transform tw-scale-100 tw-opacity-100"
                leave-active-class="tw-transition tw-duration-75 tw-ease-in"
                leave-from-class="tw-transform tw-scale-100 tw-opacity-100"
                leave-to-class="tw-transform tw-scale-95 tw-opacity-0"
            >
                <div
                    v-if="isOpen"
                    :class="[
                        'tw-absolute tw-z-50 tw-mt-1 tw-w-full tw-bg-white tw-shadow-lg tw-border tw-border-solid tw-border-gray-200 tw-rounded-md tw-py-1',
                        $root.$i18n.locale == 'ar' ? 'tw-right-0' : 'tw-left-0'
                    ]"
                >
                    <button
                        v-for="type in accountTypes"
                        :key="type.value"
                        type="button"
                        @click="selectType(type.value)"
                        :class="[
                            'tw-w-full tw-px-4 tw-py-3 tw-text-left tw-text-sm tw-duration-200 hover:tw-bg-gray-50 tw-flex tw-flex-col tw-gap-1',
                            selectedType === type.value && 'tw-bg-primary-50 tw-text-primary-600',
                            $root.$i18n.locale == 'ar' && 'tw-text-right'
                        ]"
                    >
                        <span class="tw-font-semibold">
                            {{ t(`account-types.${type.value}.title`) }}
                        </span>
                        <span :class="[
                            'tw-text-xs tw-leading-relaxed',
                            selectedType === type.value ? 'tw-text-primary-500' : 'tw-text-gray-600'
                        ]">
                            {{ t(`account-types.${type.value}.description`) }}
                        </span>
                    </button>
                </div>
            </transition>

            <!-- Selected type description (when closed) -->
            <transition
                enter-active-class="tw-transition tw-duration-300 tw-ease-out"
                enter-from-class="tw-transform tw-translate-y-[-10px] tw-opacity-0"
                enter-to-class="tw-transform tw-translate-y-0 tw-opacity-100"
                leave-active-class="tw-transition tw-duration-200 tw-ease-in"
                leave-from-class="tw-transform tw-translate-y-0 tw-opacity-100"
                leave-to-class="tw-transform tw-translate-y-[-10px] tw-opacity-0"
            >
                <div
                    v-if="selectedType "
                    :class="[
                        'tw-mt-2 tw-p-3 tw-bg-gray-50 tw-border tw-border-solid tw-border-gray-200 tw-rounded-md',
                        'tw-flex tw-items-start tw-gap-3'
                    ]"
                >
                    <icon 
                        :icon="getTypeIcon(selectedType)" 
                        class="tw-text-lg tw-text-primary-500 tw-mt-0.5 tw-flex-shrink-0" 
                    />
                    <div :class="[$root.$i18n.locale == 'ar' && 'tw-text-right']">
                        <p class="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-1">
                            {{ t(`account-types.${selectedType}.title`) }}
                        </p>
                        <p class="tw-text-xs tw-text-gray-600 tw-leading-relaxed">
                            {{ t(`account-types.${selectedType}.description`) }}
                        </p>
                    </div>
                </div>
            </transition>
        </div>

        <!-- Error message -->
        <FormErrorMessage v-if="!hideErrors">
            {{ error }}
        </FormErrorMessage>

        <!-- Helper message -->
        <FormHelperMessage v-if="help">
            {{ help }}
        </FormHelperMessage>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineEmits, defineProps } from 'vue'
import { useI18n } from 'vue-i18n'
import { v4 as uuid } from 'uuid'
import FormLabel from './FormLabel.vue'
import FormErrorMessage from './FormErrorMessage.vue'
import FormHelperMessage from './FormHelperMessage.vue'

const { t } = useI18n()
const emit = defineEmits(['update:modelValue'])
const props = defineProps({
    modelValue: [String, Number],
    label: String,
    placeholder: {
        type: String,
        default: 'Select account type'
    },
    required: Boolean,
    error: String,
    help: String,
    leftIcon: {
        type: String,
        default: 'solar:user-id-bold-duotone'
    },
    hideLabel: {
        type: Boolean,
        default: false
    },
    hideErrors: {
        type: Boolean,
        default: false
    }
})

const fieldId = ref(`account-type-${uuid()}`)
const isOpen = ref(false)

const accountTypes = [
    { value: 'person' },
    { value: 'company' },
    { value: 'speaker' },
    { value: 'buyer' }
]

const selectedType = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
})

const toggleDropdown = () => {
    isOpen.value = !isOpen.value
}

const selectType = (type) => {
    selectedType.value = type
    isOpen.value = false
}

const getTypeIcon = (type) => {
    const icons = {
        person: 'solar:user-bold-duotone',
        company: 'solar:buildings-2-bold-duotone',
        speaker: 'solar:microphone-bold-duotone',
        buyer: 'solar:bag-2-bold-duotone'
    }
    return icons[type] || 'solar:user-bold-duotone'
}

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
    if (!event.target.closest(`#${fieldId.value}`) && !event.target.closest('.tw-absolute')) {
        isOpen.value = false
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Additional styles if needed */
</style> 